import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { SectionHeader } from '../components/SectionHeader';
import { WEB3FORMS_ENDPOINT, WEB3FORMS_ACCESS_KEY } from '../lib/web3forms';
type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';
export function PreQualification() {
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const companyName = data.get('companyName') as string || '';
    // Web3Forms required + helpful fields
    data.append('access_key', WEB3FORMS_ACCESS_KEY);
    data.append('subject', `Pre-Qualification Application — ${companyName}`.trim());
    data.append('from_name', '888 Sourcing Limited Website');
    setStatus('submitting');
    try {
      const res = await fetch(WEB3FORMS_ENDPOINT, {
        method: 'POST',
        body: data,
        headers: {
          Accept: 'application/json'
        }
      });
      const result = await res.json().catch(() => ({}));
      if (res.ok && result.success) {
        setStatus('success');
        form.reset();
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };
  return <div className="pt-32 pb-24 min-h-screen relative overflow-hidden" data-id="element-180">
      {/* Background accent */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-navy-light/50 -skew-x-12 translate-x-32 -z-10" data-id="element-181" />

      <div className="max-w-4xl mx-auto px-6 md:px-12" data-id="element-182">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} data-id="element-183">
          <SectionHeader eyebrow="Apply Now" title="Pre-Qualification Form" description="Join our global network of verified suppliers. Please provide detailed information about your company's capabilities, certifications, and operational capacity." centered data-id="element-184" />

          {status === 'success' && <div role="status" className="mt-12 flex items-start gap-3 rounded border border-green-500/30 bg-green-500/10 px-5 py-4 text-green-300" data-id="element-185">
              <CheckCircle2 size={20} className="shrink-0 mt-0.5" data-id="element-186" />
              <div data-id="element-187">
                <p className="font-semibold text-green-200" data-id="element-188">
                  Application received
                </p>
                <p className="text-sm" data-id="element-189">
                  Thank you for your interest. Our procurement team will review
                  your details and contact you within 3–5 business days.
                </p>
              </div>
            </div>}

          <form className="bg-navy-card p-8 md:p-12 rounded-sm border border-navy-border shadow-2xl mt-12" onSubmit={handleSubmit} data-id="element-190">
            <h3 className="text-xl font-bold text-white mb-6 pb-4 border-b border-navy-border" data-id="element-191">
              Company Information
            </h3>

            <div className="grid md:grid-cols-2 gap-6 mb-6" data-id="element-192">
              <div data-id="element-193">
                <label htmlFor="companyName" className="block text-sm font-medium text-slate-300 mb-2" data-id="element-194">
                  Company Name *
                </label>
                <input type="text" id="companyName" name="companyName" className="w-full bg-navy border border-navy-border rounded px-4 py-3 text-white focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors" placeholder="Company Ltd." required data-id="element-195" />
              </div>
              <div data-id="element-196">
                <label htmlFor="website" className="block text-sm font-medium text-slate-300 mb-2" data-id="element-197">
                  Company Website
                </label>
                <input type="url" id="website" name="website" className="w-full bg-navy border border-navy-border rounded px-4 py-3 text-white focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors" placeholder="https://www.example.com" data-id="element-198" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-10" data-id="element-199">
              <div data-id="element-200">
                <label htmlFor="country" className="block text-sm font-medium text-slate-300 mb-2" data-id="element-201">
                  Country of Registration *
                </label>
                <input type="text" id="country" name="country" className="w-full bg-navy border border-navy-border rounded px-4 py-3 text-white focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors" placeholder="United Kingdom" required data-id="element-202" />
              </div>
              <div data-id="element-203">
                <label htmlFor="years" className="block text-sm font-medium text-slate-300 mb-2" data-id="element-204">
                  Years in Operation *
                </label>
                <input type="number" id="years" name="years" className="w-full bg-navy border border-navy-border rounded px-4 py-3 text-white focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors" placeholder="e.g. 10" required data-id="element-205" />
              </div>
            </div>

            <h3 className="text-xl font-bold text-white mb-6 pb-4 border-b border-navy-border" data-id="element-206">
              Primary Contact
            </h3>

            <div className="grid md:grid-cols-2 gap-6 mb-6" data-id="element-207">
              <div data-id="element-208">
                <label htmlFor="firstName" className="block text-sm font-medium text-slate-300 mb-2" data-id="element-209">
                  First Name *
                </label>
                <input type="text" id="firstName" name="firstName" className="w-full bg-navy border border-navy-border rounded px-4 py-3 text-white focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors" placeholder="John" required data-id="element-210" />
              </div>
              <div data-id="element-211">
                <label htmlFor="lastName" className="block text-sm font-medium text-slate-300 mb-2" data-id="element-212">
                  Last Name *
                </label>
                <input type="text" id="lastName" name="lastName" className="w-full bg-navy border border-navy-border rounded px-4 py-3 text-white focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors" placeholder="Doe" required data-id="element-213" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-10" data-id="element-214">
              <div data-id="element-215">
                <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2" data-id="element-216">
                  Business Email *
                </label>
                <input type="email" id="email" name="email" className="w-full bg-navy border border-navy-border rounded px-4 py-3 text-white focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors" placeholder="john@company.com" required data-id="element-217" />
              </div>
              <div data-id="element-218">
                <label htmlFor="phone" className="block text-sm font-medium text-slate-300 mb-2" data-id="element-219">
                  Phone Number *
                </label>
                <input type="tel" id="phone" name="phone" className="w-full bg-navy border border-navy-border rounded px-4 py-3 text-white focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors" placeholder="+44 20 7123 4567" required data-id="element-220" />
              </div>
            </div>

            <h3 className="text-xl font-bold text-white mb-6 pb-4 border-b border-navy-border" data-id="element-221">
              Capabilities & Compliance
            </h3>

            <div className="mb-6" data-id="element-222">
              <label htmlFor="sector" className="block text-sm font-medium text-slate-300 mb-2" data-id="element-223">
                Primary Sector *
              </label>
              <select id="sector" name="sector" className="w-full bg-navy border border-navy-border rounded px-4 py-3 text-white focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors appearance-none" required data-id="element-224">
                <option value="" data-id="element-225">Select a sector...</option>
                <option value="healthcare" data-id="element-226">Healthcare & Medical</option>
                <option value="infrastructure" data-id="element-227">
                  Infrastructure & Construction
                </option>
                <option value="agriculture" data-id="element-228">Agriculture & Food Security</option>
                <option value="energy" data-id="element-229">Energy & Power</option>
                <option value="technology" data-id="element-230">Technology & Telecoms</option>
                <option value="defense" data-id="element-231">Defense & Security</option>
                <option value="other" data-id="element-232">Other</option>
              </select>
            </div>

            <div className="mb-6" data-id="element-233">
              <label htmlFor="certifications" className="block text-sm font-medium text-slate-300 mb-2" data-id="element-234">
                Key Certifications (e.g., ISO 9001, CE, FDA)
              </label>
              <input type="text" id="certifications" name="certifications" className="w-full bg-navy border border-navy-border rounded px-4 py-3 text-white focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors" placeholder="List relevant certifications..." data-id="element-235" />
            </div>

            <div className="mb-8" data-id="element-236">
              <label htmlFor="capabilities" className="block text-sm font-medium text-slate-300 mb-2" data-id="element-237">
                Core Capabilities & Products *
              </label>
              <textarea id="capabilities" name="capabilities" rows={5} className="w-full bg-navy border border-navy-border rounded px-4 py-3 text-white focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors resize-none" placeholder="Briefly describe your manufacturing capacity, main product lines, and distribution capabilities..." required data-id="element-238"></textarea>
            </div>

            <button type="submit" disabled={status === 'submitting'} className="w-full bg-gold hover:bg-gold-light text-navy font-bold py-4 rounded transition-colors text-lg flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-239">
              {status === 'submitting' ? <>
                  <Loader2 size={20} className="animate-spin" data-id="element-240" /> Submitting…
                </> : 'Submit Pre-Qualification Application'}
            </button>

            {status === 'error' && <div role="alert" className="mt-4 flex items-start gap-3 rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-id="element-241">
                <AlertCircle size={18} className="shrink-0 mt-0.5" data-id="element-242" />
                <span data-id="element-243">
                  Something went wrong submitting your application. Please try
                  again, or email us directly at info@888sourcinngltd.co.uk.
                </span>
              </div>}

            <p className="text-xs text-slate-500 text-center mt-4" data-id="element-244">
              By submitting this form, you agree to our verification process.
              Our procurement team will review your details and contact you
              within 3-5 business days.
            </p>
          </form>
        </motion.div>
      </div>
    </div>;
}